import { useState } from 'react';
import { useParams, Link } from 'react-router';
import {
  ArrowLeft, TrendingUp, BarChart3, Trophy,
  Clock, Target, Signal, Eye, EyeOff, Plus, Layers, BadgeCheck,
  Pencil, Trash2, MoreVertical,
} from 'lucide-react';
import {
  useProvider, useProviderTrades, useProviderSignals,
  useCreateSignal, useUpdateSignal, useDeleteSignal, useCopyPrices,
  type CopySignal, type ProviderTrade,
} from '@/hooks/use-copy-trade';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Drawer } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { formatPnl, pnlColor, formatPrice, getClientContractSize } from '@/lib/format';

type Tab = 'overview' | 'signals' | 'positions' | 'history';

export default function CopyProviderAdminDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: provider } = useProvider(id!);
  const { data: openTradesData } = useProviderTrades(id!, { status: 'OPEN', limit: 100 });
  const { data: signals } = useProviderSignals(id!);
  const { data: livePrices } = useCopyPrices();
  const [tab, setTab] = useState<Tab>('overview');
  const [showCreateSignal, setShowCreateSignal] = useState(false);

  const openTrades = openTradesData?.trades ?? [];

  // Compute floating P&L
  let floatingPnl = 0;
  if (livePrices && openTrades.length > 0) {
    for (const t of openTrades) {
      const price = livePrices[t.symbol];
      if (!price) continue;
      const cp = t.type === 'BUY' ? price.bid : price.ask;
      const diff = t.type === 'BUY' ? cp - t.open_price : t.open_price - cp;
      floatingPnl += diff * t.volume * getClientContractSize(t.symbol, livePrices || undefined);
    }
    floatingPnl = parseFloat(floatingPnl.toFixed(2));
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <Link to="/copy-trade/admin" className="text-xs text-ink-3 hover:text-orange flex items-center gap-1 mb-3">
          <ArrowLeft size={12} /> Back to Admin
        </Link>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {provider?.avatar_url && (
              <img src={provider.avatar_url} alt="" className="w-11 h-11 rounded-full bg-surface flex-shrink-0" />
            )}
            <div>
              <h1 className="text-xl font-bold text-ink">{provider?.display_name || 'Provider Detail'}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] font-mono text-ink-4">{provider?.external_id || id?.slice(0, 8)}</span>
                {provider?.symbol && (
                  <span className="text-[10px] font-mono font-semibold text-orange bg-orange/10 rounded px-1.5 py-0.5">{provider.symbol}</span>
                )}
                {provider?.source && (
                  <Badge color="default">{provider.source}</Badge>
                )}
                {provider && !provider.is_active && (
                  <Badge color="red">Inactive</Badge>
                )}
              </div>
            </div>
          </div>
          <Button size="sm" onClick={() => setShowCreateSignal(true)}>
            <Plus size={12} className="mr-1" /> Create Signal
          </Button>
        </div>
      </div>

      {/* Provider details card */}
      {provider && (
        <div className="glass-sm rounded-lg p-4 border border-glass-border">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 text-xs">
            <div>
              <span className="text-ink-4">Strategy No</span>
              <p className="font-mono font-semibold text-ink mt-0.5">{provider.external_strategy_no || '—'}</p>
            </div>
            <div>
              <span className="text-ink-4">Vantage Account</span>
              <p className="font-semibold text-ink mt-0.5">{(provider as any).vantageAccount?.name || (provider as any).vantageAccount?.account_id || '—'}</p>
            </div>
            <div>
              <span className="text-ink-4">Total P&L</span>
              <p className={`font-mono font-semibold mt-0.5 ${pnlColor(provider.total_pnl)}`}>{formatPnl(provider.total_pnl)}</p>
            </div>
            <div>
              <span className="text-ink-4">Win Rate</span>
              <p className="font-mono font-semibold text-ink mt-0.5">{provider.total_trades > 0 ? `${provider.win_rate.toFixed(0)}%` : '—'}</p>
            </div>
            <div>
              <span className="text-ink-4">Total Trades</span>
              <p className="font-mono font-semibold text-ink mt-0.5">{provider.total_trades}</p>
            </div>
            <div>
              <span className="text-ink-4">Won / Lost</span>
              <p className="font-mono font-semibold text-ink mt-0.5">{provider.winning_trades} / {provider.losing_trades}</p>
            </div>
            <div>
              <span className="text-ink-4">Provider Equity</span>
              <p className="font-mono font-semibold text-ink mt-0.5">
                {provider.provider_equity ? `$${provider.provider_equity.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
              </p>
            </div>
            <div>
              <span className="text-ink-4">Created</span>
              <p className="font-semibold text-ink mt-0.5">{new Date(provider.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <span className="text-ink-4">Verified</span>
              <p className="font-semibold text-ink mt-0.5">{provider.is_verified ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-border">
        {([
          { key: 'overview', label: 'Overview', icon: BarChart3 },
          { key: 'signals', label: 'Signals', icon: Signal },
          { key: 'positions', label: 'Positions', icon: Layers },
          { key: 'history', label: 'Trading History', icon: Clock },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-colors cursor-pointer border-b-2 -mb-px ${
              tab === t.key ? 'border-orange text-orange' : 'border-transparent text-ink-3 hover:text-ink'
            }`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <OverviewTab
          openTrades={openTrades}
          floatingPnl={floatingPnl}
          strategyMetadata={provider?.strategy_metadata}
        />
      )}
      {tab === 'signals' && (
        <SignalsTab signals={signals ?? []} onCreateSignal={() => setShowCreateSignal(true)} />
      )}
      {tab === 'positions' && <PositionsTab providerId={id!} livePrices={livePrices} />}
      {tab === 'history' && <HistoryTab providerId={id!} livePrices={livePrices} />}

      {/* Create signal modal */}
      <CreateSignalDrawer open={showCreateSignal} providerId={id!} provider={provider} onClose={() => setShowCreateSignal(false)} />
    </div>
  );
}

// ─── OVERVIEW TAB ──────────────────────────────────────────────────────

function OverviewTab({
  openTrades,
  floatingPnl,
  strategyMetadata,
}: {
  openTrades: ProviderTrade[];
  floatingPnl: number;
  strategyMetadata?: Record<string, any> | null;
}) {
  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={TrendingUp} label="Floating P&L" value={openTrades.length > 0 ? formatPnl(floatingPnl) : '—'} color={pnlColor(floatingPnl)} />
        <StatCard icon={Target} label="Open Positions" value={String(openTrades.length)} />
        <StatCard icon={Trophy} label="Symbols" value={[...new Set(openTrades.map(t => t.symbol))].length.toString()} />
        <StatCard icon={BarChart3} label="Active Since" value={strategyMetadata?.activeSince || '—'} />
      </div>

      {/* Strategy Info */}
      {strategyMetadata && <StrategyInfoCard metadata={strategyMetadata} />}
    </div>
  );
}

// ─── SIGNALS TAB ───────────────────────────────────────────────────────

function SignalsTab({ signals, onCreateSignal }: { signals: CopySignal[]; onCreateSignal: () => void }) {
  const [editingSignal, setEditingSignal] = useState<CopySignal | null>(null);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-ink flex items-center gap-2">
          <Signal size={14} className="text-orange" />
          Signals ({signals.length})
        </h2>
        <Button size="sm" onClick={onCreateSignal}>
          <Plus size={12} className="mr-1" /> Create Signal
        </Button>
      </div>
      {signals.length === 0 ? (
        <div className="glass-sm rounded-lg p-4 text-center border border-border">
          <p className="text-xs text-ink-4">No signals created yet. Create one to start tracking paper trades.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {signals.map(signal => (
            <SignalCard key={signal.id} signal={signal} onEdit={setEditingSignal} />
          ))}
        </div>
      )}

      {editingSignal && (
        <EditSignalDrawer signal={editingSignal} onClose={() => setEditingSignal(null)} />
      )}
    </div>
  );
}

// ─── POSITIONS TAB ─────────────────────────────────────────────────────

function PositionsTab({ providerId, livePrices }: { providerId: string; livePrices?: Record<string, { ask: number; bid: number }> }) {
  const { data: tradesData } = useProviderTrades(providerId, { status: 'OPEN', limit: 100 });
  const trades = tradesData?.trades ?? [];

  if (trades.length === 0) {
    return <div className="text-center py-12"><p className="text-xs text-ink-4">No open positions</p></div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-ink-4">
            <th className="text-left px-4 py-3 font-medium">Symbol</th>
            <th className="text-left px-4 py-3 font-medium">Type</th>
            <th className="text-right px-4 py-3 font-medium">Volume</th>
            <th className="text-right px-4 py-3 font-medium">Open</th>
            <th className="text-right px-4 py-3 font-medium">Current</th>
            <th className="text-right px-4 py-3 font-medium">P&L</th>
            <th className="text-left px-4 py-3 font-medium">Time</th>
          </tr>
        </thead>
        <tbody>
          {trades.map(t => <TradeRow key={t.id} trade={t} livePrices={livePrices} />)}
        </tbody>
      </table>
    </div>
  );
}

// ─── HISTORY TAB ───────────────────────────────────────────────────────

function HistoryTab({ providerId, livePrices }: { providerId: string; livePrices?: Record<string, { ask: number; bid: number }> }) {
  const [page, setPage] = useState(1);
  const { data: tradesData } = useProviderTrades(providerId, { status: 'CLOSED', limit: 20, page });
  const trades = tradesData?.trades ?? [];
  const totalPages = Math.ceil((tradesData?.total ?? 0) / 20);

  if (trades.length === 0) {
    return <div className="text-center py-12"><p className="text-xs text-ink-4">No closed trades yet</p></div>;
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-ink-4">
              <th className="text-left px-4 py-3 font-medium">Symbol</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-right px-4 py-3 font-medium">Volume</th>
              <th className="text-right px-4 py-3 font-medium">Open</th>
              <th className="text-right px-4 py-3 font-medium">Close</th>
              <th className="text-right px-4 py-3 font-medium">P&L</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {trades.map(t => <TradeRow key={t.id} trade={t} livePrices={livePrices} />)}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</Button>
          <span className="text-xs text-ink-3">{page} / {totalPages}</span>
          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
        </div>
      )}
    </div>
  );
}

// ─── SHARED COMPONENTS ─────────────────────────────────────────────────

function TradeRow({ trade: t, livePrices }: { trade: ProviderTrade; livePrices?: Record<string, { ask: number; bid: number }> }) {
  let pnl = t.gross_pnl;
  let closePrice: number | null = t.close_price;
  if (t.status === 'OPEN' && livePrices) {
    const p = livePrices[t.symbol];
    if (p) {
      closePrice = t.type === 'BUY' ? p.bid : p.ask;
      const diff = t.type === 'BUY' ? closePrice - t.open_price : t.open_price - closePrice;
      pnl = parseFloat((diff * t.volume * getClientContractSize(t.symbol, livePrices || undefined)).toFixed(2));
    }
  }
  const date = new Date(t.opened_at);

  return (
    <tr className="border-b border-border hover:bg-surface/50 transition-colors">
      <td className="px-4 py-3.5 font-semibold text-ink">{t.symbol}</td>
      <td className="px-4 py-3.5"><Badge color={t.type === 'BUY' ? 'green' : 'red'}>{t.type === 'BUY' ? 'Buy' : 'Sell'}</Badge></td>
      <td className="px-4 py-3.5 text-right font-mono text-ink-3">{t.volume < 0.01 ? t.volume.toFixed(4) : t.volume.toFixed(2)}</td>
      <td className="px-4 py-3.5 text-right font-mono text-ink-3">{formatPrice(t.open_price, t.symbol)}</td>
      <td className="px-4 py-3.5 text-right font-mono text-ink-3">
        {closePrice != null ? formatPrice(closePrice, t.symbol) : '—'}
      </td>
      <td className={`px-4 py-3.5 text-right font-mono font-semibold ${pnlColor(pnl)}`}>
        {pnl !== 0 ? formatPnl(pnl) : '—'}
      </td>
      <td className="px-4 py-3.5 text-ink-4">
        {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </td>
    </tr>
  );
}

function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: string; color?: string }) {
  return (
    <div className="glass-sm rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} className="text-ink-4" />
        <span className="text-[10px] text-ink-4 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-lg font-bold font-mono ${color || 'text-ink'}`}>{value}</p>
    </div>
  );
}

function StrategyInfoCard({ metadata }: { metadata: Record<string, any> }) {
  const settlementMap: Record<string, string> = { '1': 'Daily', '2': 'Weekly', '3': 'Monthly' };
  const fmt = (v: any, suffix = '') => {
    if (v == null || v === '') return '—';
    const n = parseFloat(v);
    return isNaN(n) ? String(v) : `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}${suffix}`;
  };

  const rows: { label: string; value: string }[] = [
    { label: 'Cumulative Copiers', value: fmt(metadata.cumulativeCopiers) },
    { label: 'Active Copiers', value: fmt(metadata.activeCopiers) },
    { label: 'AUM', value: metadata.copierAum ? `${fmt(metadata.copierAum)} ${metadata.currency || 'USD'}` : '—' },
    { label: 'Settlement Frequency', value: settlementMap[metadata.settlementFrequency] || metadata.settlementFrequency || '—' },
    { label: 'Profit Sharing', value: metadata.profitShareRate ? `${(parseFloat(metadata.profitShareRate) * 100).toFixed(0)}%` : '—' },
    { label: 'Average Holding Time', value: fmt(metadata.averageHoldingTime, ' Hours') },
    { label: 'Trades Per Week', value: fmt(metadata.averageWeeklyTrades) },
    { label: 'Profitable Weeks', value: fmt(metadata.profitableWeeks) },
    { label: 'Active Since', value: metadata.activeSince || '—' },
    { label: 'Investment Amount', value: metadata.investmentAmount ? fmt(metadata.investmentAmount, ` ${metadata.currency || 'USD'}`) : '—' },
    { label: 'Daily Max Drawdown', value: metadata.dailyMaxDrawDown && metadata.dailyMaxDrawDown !== '0.00000' ? `${(parseFloat(metadata.dailyMaxDrawDown) * 100).toFixed(2)}%` : '—' },
    { label: 'Yearly Max Drawdown', value: metadata.yearlyMaxDrawDown && metadata.yearlyMaxDrawDown !== '0.00000' ? `${(parseFloat(metadata.yearlyMaxDrawDown) * 100).toFixed(2)}%` : '—' },
  ];

  return (
    <div className="glass-sm rounded-lg border border-glass-border p-4">
      <h3 className="text-sm font-bold text-ink mb-3 flex items-center gap-2">
        <BarChart3 size={14} className="text-orange" /> Strategy Info
      </h3>
      <div className="divide-y divide-border">
        {rows.map(r => (
          <div key={r.label} className="flex items-center justify-between py-2.5">
            <span className="text-xs text-ink-3">{r.label}</span>
            <span className="text-xs font-bold font-mono text-ink">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignalCard({ signal, onEdit }: { signal: CopySignal; onEdit: (s: CopySignal) => void }) {
  const updateSignal = useUpdateSignal();
  const deleteSignal = useDeleteSignal();
  const [showMenu, setShowMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const symbolDisplay = signal.symbol_filter?.join(', ') || 'All Symbols';

  return (
    <div className="glass-sm rounded-lg p-3 border border-glass-border hover:border-orange/30 transition-all">
      <div className="flex items-center justify-between">
        <Link to={`/copy-trade/${signal.id}`} className="flex items-center gap-2 flex-1 min-w-0">
          <Signal size={14} className="text-orange flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-ink truncate">{signal.name}</span>
              {signal.is_verified && <BadgeCheck size={12} className="text-blue-500 flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-ink-4 mt-0.5">
              <span>{symbolDisplay}</span>
              {signal.default_lot_size && <span>{signal.default_lot_size} lot</span>}
              <span>{signal.total_trades} trades</span>
              <span className={pnlColor(signal.total_pnl)}>{formatPnl(signal.total_pnl)}</span>
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
          {signal.is_visible ? (
            <Badge color="green"><Eye size={9} className="mr-0.5" /> Published</Badge>
          ) : (
            <Badge color="default"><EyeOff size={9} className="mr-0.5" /> Hidden</Badge>
          )}
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-md hover:bg-surface-2 text-ink-3 hover:text-ink transition-colors cursor-pointer">
              <MoreVertical size={14} />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-8 z-50 bg-[var(--popup-bg)] border border-border rounded-lg shadow-modal py-1 min-w-[140px]">
                  <button onClick={() => { setShowMenu(false); onEdit(signal); }}
                    className="w-full text-left px-3 py-2 text-xs text-ink hover:bg-surface flex items-center gap-2 cursor-pointer">
                    <Pencil size={12} /> Edit
                  </button>
                  <button onClick={() => {
                    setShowMenu(false);
                    updateSignal.mutate({ id: signal.id, data: signal.is_visible ? { is_visible: false, is_active: false } : { is_visible: true, is_active: true } } as any);
                  }}
                    className="w-full text-left px-3 py-2 text-xs text-ink hover:bg-surface flex items-center gap-2 cursor-pointer">
                    {signal.is_visible ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                  </button>
                  <div className="border-t border-border my-1" />
                  <button onClick={() => { setShowMenu(false); setConfirmDelete(true); }}
                    className="w-full text-left px-3 py-2 text-xs text-red hover:bg-red-bg flex items-center gap-2 cursor-pointer">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="mt-3 p-2.5 rounded-md bg-red-bg border border-red/20">
          <p className="text-[11px] text-red font-semibold mb-2">Delete "{signal.name}"? This cannot be undone.</p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button size="sm" onClick={() => deleteSignal.mutate(signal.id)}
              className="!bg-red !text-white">
              {deleteSignal.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function EditSignalDrawer({ signal, onClose }: { signal: CopySignal; onClose: () => void }) {
  const updateSignal = useUpdateSignal();
  const [form, setForm] = useState({
    name: signal.name,
    description: signal.description || '',
    symbol_filter: signal.symbol_filter?.join(', ') || '',
    direction_filter: signal.direction_filter || 'all' as 'all' | 'buy' | 'sell',
    min_capital: signal.min_capital != null ? String(signal.min_capital) : '',
    account_type: signal.account_type || 'STANDARD' as 'STANDARD' | 'CENT' | 'MINI' | 'NANO',
    reference_equity: signal.reference_equity != null ? String(signal.reference_equity) : '',
  });
  const update = (f: string, v: string) => setForm(p => ({ ...p, [f]: v }));

  return (
    <Drawer open={true} title={`Edit "${signal.name}"`} onClose={onClose}>
      <div className="space-y-4">
        {/* Branding */}
        <div>
          <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider mb-2">Branding</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Signal Name *</label>
              <Input value={form.name} onChange={e => update('name', e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Description</label>
              <textarea value={form.description} onChange={e => update('description', e.target.value)} rows={3}
                className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:border-orange resize-none" />
            </div>
          </div>
        </div>

        {/* Filtering */}
        <div>
          <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider mb-2">Filtering</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Symbols</label>
              <Input value={form.symbol_filter} onChange={e => update('symbol_filter', e.target.value)} placeholder="XAUUSD+, GBPUSD+" />
              <p className="text-[10px] text-ink-4 mt-1">Comma-separated. Leave empty to include all provider trades.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Direction</label>
              <div className="grid grid-cols-3 gap-2">
                {(['all', 'buy', 'sell'] as const).map(d => (
                  <button key={d} type="button" onClick={() => setForm(p => ({ ...p, direction_filter: d }))}
                    className={`text-center rounded-lg border px-3 py-2 transition-all cursor-pointer ${
                      form.direction_filter === d ? 'border-orange/40 bg-orange/5' : 'border-glass-border bg-surface/50 hover:bg-surface'
                    }`}>
                    <span className={`text-[11px] font-bold capitalize ${form.direction_filter === d ? 'text-orange' : 'text-ink-3'}`}>
                      {d === 'all' ? 'All' : d === 'buy' ? 'Buy Only' : 'Sell Only'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Rules */}
        <div>
          <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider mb-2">Subscription Rules</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Minimum Capital ($)</label>
              <Input type="number" value={form.min_capital} onChange={e => update('min_capital', e.target.value)} placeholder="e.g. 100" />
              <p className="text-[10px] text-ink-4 mt-1">Minimum capital required to copy this signal. Leave empty for no minimum.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Account Type</label>
              <select
                value={form.account_type}
                onChange={e => setForm(p => ({ ...p, account_type: e.target.value as any }))}
                className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:border-orange cursor-pointer"
              >
                <option value="STANDARD">Standard (as-is)</option>
                <option value="CENT">Cent (÷ 100)</option>
                <option value="MINI">Mini (× 10)</option>
                <option value="NANO">Nano (× 100)</option>
              </select>
              <p className="text-[10px] text-ink-4 mt-1">Volume normalization applied to provider trades before allocation.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Reference Equity ($)</label>
              <Input type="number" value={form.reference_equity} onChange={e => update('reference_equity', e.target.value)} placeholder="e.g. 1000" />
              <p className="text-[10px] text-ink-4 mt-1">Capital level where copier matches provider's position size 1:1. Leave empty to use auto-detected value.</p>
            </div>
          </div>
        </div>

        <Button onClick={() => {
          if (!form.name) return;
          const symbols = form.symbol_filter.split(',').map(s => s.trim()).filter(Boolean);
          updateSignal.mutate({
            id: signal.id,
            data: {
              name: form.name,
              description: form.description || null,
              symbol_filter: symbols.length > 0 ? symbols : null,
              direction_filter: form.direction_filter,
              min_capital: form.min_capital ? parseFloat(form.min_capital) : null,
              reference_equity: form.reference_equity ? parseFloat(form.reference_equity) : null,
              account_type: form.account_type,
            } as any,
          }, { onSuccess: onClose });
        }} disabled={!form.name || updateSignal.isPending} className="w-full">
          {updateSignal.isPending ? <Spinner size="sm" /> : 'Save Changes'}
        </Button>
      </div>
    </Drawer>
  );
}

function CreateSignalDrawer({ open, providerId, provider, onClose }: {
  open: boolean;
  providerId: string;
  provider?: any;
  onClose: () => void;
}) {
  const createSignal = useCreateSignal();
  const [form, setForm] = useState({
    name: provider?.display_name || '',
    description: provider?.strategy_metadata?.description || provider?.description || '',
    symbol_filter: '',
    direction_filter: 'all' as 'all' | 'buy' | 'sell',
    min_capital: '',
    reference_equity: '',
    account_type: 'STANDARD' as 'STANDARD' | 'CENT' | 'MINI' | 'NANO',
  });
  const update = (f: string, v: string) => setForm(p => ({ ...p, [f]: v }));

  return (
    <Drawer open={open} title="Create Signal" onClose={onClose}>
      <div className="space-y-4">
        {/* Branding */}
        <div>
          <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider mb-2">Branding</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Signal Name *</label>
              <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="e.g. SATORI Gold Conservative" />
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Description</label>
              <textarea value={form.description} onChange={e => update('description', e.target.value)} rows={3}
                className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:border-orange resize-none"
                placeholder="What users will see about this signal..." />
            </div>
          </div>
        </div>

        {/* Filtering */}
        <div>
          <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider mb-2">Filtering</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Symbols</label>
              <Input value={form.symbol_filter} onChange={e => update('symbol_filter', e.target.value)} placeholder="XAUUSD+, GBPUSD+" />
              <p className="text-[10px] text-ink-4 mt-1">Comma-separated. Leave empty to include all provider trades.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Direction</label>
              <div className="grid grid-cols-3 gap-2">
                {(['all', 'buy', 'sell'] as const).map(d => (
                  <button key={d} type="button" onClick={() => setForm(p => ({ ...p, direction_filter: d }))}
                    className={`text-center rounded-lg border px-3 py-2 transition-all cursor-pointer ${
                      form.direction_filter === d ? 'border-orange/40 bg-orange/5' : 'border-glass-border bg-surface/50 hover:bg-surface'
                    }`}>
                    <span className={`text-[11px] font-bold capitalize ${form.direction_filter === d ? 'text-orange' : 'text-ink-3'}`}>
                      {d === 'all' ? 'All' : d === 'buy' ? 'Buy Only' : 'Sell Only'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Rules */}
        <div>
          <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider mb-2">Subscription Rules</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Minimum Capital ($)</label>
              <Input type="number" value={form.min_capital} onChange={e => update('min_capital', e.target.value)} placeholder="e.g. 100" />
              <p className="text-[10px] text-ink-4 mt-1">Minimum capital required to copy. Leave empty for no minimum.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Account Type</label>
              <select
                value={form.account_type}
                onChange={e => setForm(p => ({ ...p, account_type: e.target.value as any }))}
                className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:border-orange cursor-pointer"
              >
                <option value="STANDARD">Standard (as-is)</option>
                <option value="CENT">Cent (÷ 100)</option>
                <option value="MINI">Mini (× 10)</option>
                <option value="NANO">Nano (× 100)</option>
              </select>
              <p className="text-[10px] text-ink-4 mt-1">Volume normalization applied to provider trades before allocation.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Reference Equity ($)</label>
              <Input type="number" value={form.reference_equity} onChange={e => update('reference_equity', e.target.value)} placeholder="e.g. 1000" />
              <p className="text-[10px] text-ink-4 mt-1">Capital level where copier matches provider's position size 1:1.</p>
            </div>
          </div>
        </div>

        <Button onClick={() => {
          if (!form.name) return;
          const symbols = form.symbol_filter.split(',').map(s => s.trim()).filter(Boolean);
          createSignal.mutate({
            providerId,
            name: form.name,
            description: form.description || undefined,
            symbol_filter: symbols.length > 0 ? symbols : undefined,
            direction_filter: form.direction_filter,
            min_capital: form.min_capital ? parseFloat(form.min_capital) : undefined,
            reference_equity: form.reference_equity ? parseFloat(form.reference_equity) : undefined,
            account_type: form.account_type,
          } as any, { onSuccess: onClose });
        }} disabled={!form.name || createSignal.isPending} className="w-full">
          {createSignal.isPending ? <Spinner size="sm" /> : 'Create Signal'}
        </Button>
      </div>
    </Drawer>
  );
}
